import React, {useEffect, useMemo, useState} from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  ListItemText,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  IconButton,
  Tooltip,
  Toolbar,
  Typography
} from '@mui/material';

import { Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, Close as CloseIcon, Visibility, CheckBox } from '@mui/icons-material';
import { UserSettings } from '@/api/userSettings';
import DialogWrapper from '@/components/DialogWrapper';

interface Props {
  users: UserSettings[];
  loading: boolean;
  onAction: (user: UserSettings, editMode: boolean) => void;
  onBulkEdit: (users: UserSettings[]) => void;
  onBulkDelete: (users: UserSettings[]) => Promise<void>;
  onDeleteUser: (user: UserSettings) => Promise<void>;
}

type SortField = 'name' | 'email' | 'role' | 'business_unit' | 'job_title'| 'department'| 'office_location' | 'is_admin' | 'is_active';
type SortDirection = 'asc' | 'desc';
type StatusFilter = 'all' | 'active' | 'suspended';
type IsAdminFilter = 'all' | 'yes' | 'no';

const ROWS_PER_PAGE = 15;
const ALL_BUSINESS_UNITS = '__all_business_units__';
const ALL_DEPARTMENTS = '__all_departments__';
const ALL_LOCATIONS = '__all_locations__';

const UsersTab: React.FC<Props> = ({ users, loading, onAction, onBulkEdit, onBulkDelete, onDeleteUser }) => {

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [businessUnitFilter, setBusinessUnitFilter] = useState<string[]>([ALL_BUSINESS_UNITS]);
  const [departmentFilter, setDepartmentFilter] = useState<string[]>([ALL_DEPARTMENTS]);
  const [locationFilter, setLocationFilter] = useState<string[]>([ALL_LOCATIONS]);
  //const [departmentFilter, setDepartmentFilter] = useState('all');
  //const [locationFilter, setLocationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [isAdminFilter, setIsAdminFilter] = useState<IsAdminFilter>('all');
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [singleDeleteDialogOpen, setSingleDeleteDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<UserSettings | null>(null);
  const [bulkDeleteSubmitting, setBulkDeleteSubmitting] = useState(false);
  const [singleDeleteSubmitting, setSingleDeleteSubmitting] = useState(false);

  const [selected, setSelected] = useState<UserSettings[]>([])

  const roleOptions = useMemo(() => {
    const roles = new Set(users.map(u => (u.role || 'user').toLowerCase()));;
    return ['all', ...Array.from(roles)];
  }, [users]);

  const businessUnitOptions = useMemo(() => {
    const units = new Set(users.map(u => u.business_unit || 'Unassigned'));
    return Array.from(units);
  }, [users]);

  const departmentOptions = useMemo(() => {
    const departments = new Set(users.map(u => u.department || 'N/A'));
    return Array.from(departments);
  }, [users]);

  const locationOptions = useMemo(() => {
    const locations = new Set(users.map(u => u.office_location || 'N/A'));
    return Array.from(locations);
  }, [users]);
  
  const normalizedStatus = (u: UserSettings) => (u.is_active !== false ? 'active' : 'suspended');
  const normalizedIsAdmin = (u: UserSettings) => (u.is_admin !== false ? 'yes' : 'no');

  const processedUsers = useMemo(() => {
    const filtered = users.filter((u) => {
      const role = (u.role || 'user').toLowerCase();
      const bu = u.business_unit || 'Unassigned';
      const department = u.department || 'N/A';
      const office_location = u.office_location || 'N/A';

      const status = normalizedStatus(u);
      const isAdmin = normalizedIsAdmin(u);

      const roleOk = roleFilter === 'all' || role === roleFilter;
      const buOk = businessUnitFilter.includes(ALL_BUSINESS_UNITS) || businessUnitFilter.includes(bu);
      const departmentOk = departmentFilter.includes(ALL_DEPARTMENTS) || departmentFilter.includes(department);
      const locationOk = locationFilter.includes(ALL_LOCATIONS) || locationFilter.includes(office_location);
      //const departmentOk = departmentFilter === 'all' || department === departmentFilter;
      //const locationOk = locationFilter === 'all' || office_location === locationFilter;
      const statusOk = statusFilter === 'all' || status === statusFilter;
      const isAdminOk = isAdminFilter === 'all' || isAdmin === isAdminFilter;

      const q = searchTerm.trim().toLowerCase();
      const name = (u.name || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      const searchOk = !q || name.includes(q) || email.includes(q);

      return roleOk && buOk && departmentOk && locationOk && statusOk && isAdminOk && searchOk;
    });

    const getSortValue = (u: UserSettings): string => {
      if (sortBy === 'is_active') return normalizedStatus(u);
      if (sortBy === 'is_admin') return normalizedIsAdmin(u);
      if (sortBy === 'business_unit') return u.business_unit || 'Unassigned';
      if (sortBy === 'role') return u.role || 'user';
      if (sortBy === 'email') return u.email || '';
      return u.name || '';
    };

    return [...filtered].sort((a, b) => {
      const av = getSortValue(a).toLowerCase();
      const bv = getSortValue(b).toLowerCase();
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, roleFilter, businessUnitFilter, departmentFilter, locationFilter, statusFilter,isAdminFilter, searchTerm, sortBy, sortDirection]);

  const pagedUsers = useMemo(() => {
    const start = page * rowsPerPage;
    return processedUsers.slice(start, start + rowsPerPage);
  }, [processedUsers, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [roleFilter, businessUnitFilter,departmentFilter,locationFilter, statusFilter,isAdminFilter, searchTerm, sortBy, sortDirection, rowsPerPage]);

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(field);
    setSortDirection('asc');
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = processedUsers.map((n) => n);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (_event: React.MouseEvent<unknown>, user: UserSettings) => {
    const selectedIndex = selected.findIndex((u) => u.id === user.id);
    let newSelected: UserSettings[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, user);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleBulkDeleteClick = () => {
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkDeleteCancel = () => {
    setBulkDeleteDialogOpen(false);
  };

  const handleBulkDeleteConfirm = async () => {
    if (!selected.length) {
      return;
    }

    setBulkDeleteSubmitting(true);
    try {
      await onBulkDelete(selected);
      setBulkDeleteDialogOpen(false);
      setSelected([]);
    } finally {
      setBulkDeleteSubmitting(false);
    }
  };

  const handleSingleDeleteClick = (user: UserSettings) => {
    setDeletingUser(user);
    setSingleDeleteDialogOpen(true);
  };

  const handleSingleDeleteCancel = () => {
    setSingleDeleteDialogOpen(false);
    setDeletingUser(null);
  };

  const handleSingleDeleteConfirm = async () => {
    if (!deletingUser) {
      return;
    }

    setSingleDeleteSubmitting(true);
    try {
      await onDeleteUser(deletingUser);
      setSingleDeleteDialogOpen(false);
      setDeletingUser(null);
    } finally {
      setSingleDeleteSubmitting(false);
    }
  };

  return (
    <>
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1 }}>
              <Tooltip title={searchOpen ? 'Hide search' : 'Search by name or email'}>
                <IconButton
                  aria-label="toggle user search"
                  color="primary"
                  onClick={() => {
                    const nextOpen = !searchOpen;
                    setSearchOpen(nextOpen);
                    if (!nextOpen) {
                      setSearchTerm('');
                    }
                  }}
                >
                  {searchOpen ? <CloseIcon fontSize="small" /> : <SearchIcon fontSize="small" />}
                </IconButton>
              </Tooltip>

              {searchOpen && (
                <TextField
                  size="small"
                  placeholder="Search name or email"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ minWidth: 240 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                select
                size="small"
                label="Role"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                sx={{ minWidth: 140 }}
              >
                {roleOptions.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role === 'all' ? 'All' : role}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Business Unit"
                value={businessUnitFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  const selected = typeof value === 'string' ? value.split(',') : value;
                  
                  // Case 1: User is selecting ALL from specific items
                  if (selected.includes(ALL_BUSINESS_UNITS) && !businessUnitFilter.includes(ALL_BUSINESS_UNITS)) {
                    setBusinessUnitFilter([ALL_BUSINESS_UNITS]);
                  }
                  // Case 2: User is deselecting ALL
                  else if (!selected.includes(ALL_BUSINESS_UNITS) && businessUnitFilter.includes(ALL_BUSINESS_UNITS)) {
                    if (selected.length > 0) {
                      setBusinessUnitFilter(selected);
                    } else {
                      setBusinessUnitFilter([ALL_BUSINESS_UNITS]);
                    }
                  }
                  // Case 3: User is adding specific items while ALL is selected
                  else if (businessUnitFilter.includes(ALL_BUSINESS_UNITS) && selected.includes(ALL_BUSINESS_UNITS) && selected.length > 1) {
                    // Remove ALL and keep only specific items
                    const specificItems = selected.filter(s => s !== ALL_BUSINESS_UNITS);
                    setBusinessUnitFilter(specificItems);
                  }
                  // Case 4: Normal multi-select or empty
                  else if (selected.length === 0) {
                    setBusinessUnitFilter([ALL_BUSINESS_UNITS]);
                  } else {
                    setBusinessUnitFilter(selected);
                  }
                }}
                sx={{ width: 250 }}
                slotProps={{
                  select: {
                    multiple: true,
                    displayEmpty: true,
                    renderValue: (selected) => {
                      const selectedBusinessUnits = selected as string[];
                      if (selectedBusinessUnits.includes(ALL_BUSINESS_UNITS)) {
                        return 'All';
                      }
                      return selectedBusinessUnits.join(', ');
                    }
                  }
                }}
              >
                <MenuItem value={ALL_BUSINESS_UNITS}>
                  <Checkbox checked={businessUnitFilter.includes(ALL_BUSINESS_UNITS)} />
                  <ListItemText primary="All" />
                </MenuItem>
                {businessUnitOptions.map((bu) => (
                  <MenuItem key={bu} value={bu}>
                    <Checkbox
                      checked={!businessUnitFilter.includes(ALL_BUSINESS_UNITS) && businessUnitFilter.includes(bu)}
                    />
                    <ListItemText primary={bu} />
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Department"
                value={departmentFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  const selected = typeof value === 'string' ? value.split(',') : value;
                  
                  // Case 1: User is selecting ALL from specific items
                  if (selected.includes(ALL_DEPARTMENTS) && !departmentFilter.includes(ALL_DEPARTMENTS)) {
                    setDepartmentFilter([ALL_DEPARTMENTS]);
                  }
                  // Case 2: User is deselecting ALL
                  else if (!selected.includes(ALL_DEPARTMENTS) && departmentFilter.includes(ALL_DEPARTMENTS)) {
                    if (selected.length > 0) {
                      setDepartmentFilter(selected);
                    } else {
                      setDepartmentFilter([ALL_DEPARTMENTS]);
                    }
                  }
                  // Case 3: User is adding specific items while ALL is selected
                  else if (departmentFilter.includes(ALL_DEPARTMENTS) && selected.includes(ALL_DEPARTMENTS) && selected.length > 1) {
                    // Remove ALL and keep only specific items
                    const specificItems = selected.filter(s => s !== ALL_DEPARTMENTS);
                    setDepartmentFilter(specificItems);
                  }
                  // Case 4: Normal multi-select or empty
                  else if (selected.length === 0) {
                    setDepartmentFilter([ALL_DEPARTMENTS]);
                  } else {
                    setDepartmentFilter(selected);
                  }
                }}
                sx={{ width: 250 }}
                slotProps={{
                  select: {
                    multiple: true,
                    displayEmpty: true,
                    renderValue: (selected) => {
                      const selectedDepartments = selected as string[];
                      if (selectedDepartments.includes(ALL_DEPARTMENTS)) {
                        return 'All';
                      }
                      return selectedDepartments.join(', ');
                    }
                  }
                }}
              >
                <MenuItem value={ALL_DEPARTMENTS}>
                  <Checkbox checked={departmentFilter.includes(ALL_DEPARTMENTS)} />
                  <ListItemText primary="All" />
                </MenuItem>
                {departmentOptions.map((department) => (
                  <MenuItem key={department} value={department}>
                    <Checkbox
                      checked={!departmentFilter.includes(ALL_DEPARTMENTS) && departmentFilter.includes(department)}
                    />
                    <ListItemText primary={department} />
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Location"
                value={locationFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  const selected = typeof value === 'string' ? value.split(',') : value;
                  
                  // Case 1: User is selecting ALL from specific items
                  if (selected.includes(ALL_LOCATIONS) && !locationFilter.includes(ALL_LOCATIONS)) {
                    setLocationFilter([ALL_LOCATIONS]);
                  }
                  // Case 2: User is deselecting ALL
                  else if (!selected.includes(ALL_LOCATIONS) && locationFilter.includes(ALL_LOCATIONS)) {
                    if (selected.length > 0) {
                      setLocationFilter(selected);
                    } else {
                      setLocationFilter([ALL_LOCATIONS]);
                    }
                  }
                  // Case 3: User is adding specific items while ALL is selected
                  else if (locationFilter.includes(ALL_LOCATIONS) && selected.includes(ALL_LOCATIONS) && selected.length > 1) {
                    // Remove ALL and keep only specific items
                    const specificItems = selected.filter(s => s !== ALL_LOCATIONS);
                    setLocationFilter(specificItems);
                  }
                  // Case 4: Normal multi-select or empty
                  else if (selected.length === 0) {
                    setLocationFilter([ALL_LOCATIONS]);
                  } else {
                    setLocationFilter(selected);
                  }
                }}
                sx={{ width: 250 }}
                slotProps={{
                  select: {
                    multiple: true,
                    displayEmpty: true,
                    renderValue: (selected) => {
                      const selectedLocations = selected as string[];
                      if (selectedLocations.includes(ALL_LOCATIONS)) {
                        return 'All';
                      }
                      return selectedLocations.join(', ');
                    }
                  }
                }}
              >
                <MenuItem value={ALL_LOCATIONS}>
                  <Checkbox checked={locationFilter.includes(ALL_LOCATIONS)} />
                  <ListItemText primary="All" />
                </MenuItem>
                {locationOptions.map((location) => (
                  <MenuItem key={location} value={location}>
                    <Checkbox
                      checked={!locationFilter.includes(ALL_LOCATIONS) && locationFilter.includes(location)}
                    />
                    <ListItemText primary={location} />
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </TextField>

              <TextField
                select
                size="small"
                label="Is Admin"
                value={isAdminFilter}
                onChange={(e) => setIsAdminFilter(e.target.value as IsAdminFilter)}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Box>
          </Box>
          <Toolbar
            sx={[
              {
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
              },
              selected.length > 1 && {
                bgcolor: (theme) =>
                  alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
              },
            ]}
          >
            {selected.length > 1 ? (
              <Typography
                sx={{ flex: '1 1 100%' }}
                color="inherit"
                variant="subtitle1"
                component="div"
              >
                {selected.length} selected
              </Typography>
            ) : (
              <Typography
                sx={{ flex: '1 1 100%' }}
                variant="h6"
                id="tableTitle"
                component="div"
              >
                {processedUsers.length} total users
              </Typography>
            )}
            {selected.length > 1 ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Edit">
                  <IconButton onClick={() => onBulkEdit(selected)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton onClick={handleBulkDeleteClick}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <></>
            )}
          </Toolbar>
          <TableContainer>  
            <Table size="small" >
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={selected.length > 0 && selected.length < processedUsers.length}
                      checked={processedUsers.length > 0 && selected.length === processedUsers.length}
                      onChange={(event) => handleSelectAllClick(event)}
                      slotProps={{
                        input: {
                          'aria-label': 'select all desserts',
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}><TableSortLabel
                      active={sortBy === 'email'}
                      direction={sortBy === 'email' ? sortDirection : 'asc'}
                      onClick={() => handleSort('email')}
                    >
                      Email
                    </TableSortLabel></TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}><TableSortLabel
                      active={sortBy === 'role'}
                      direction={sortBy === 'role' ? sortDirection : 'asc'}
                      onClick={() => handleSort('role')}
                    >
                      Role
                    </TableSortLabel></TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}><TableSortLabel
                      active={sortBy === 'business_unit'}
                      direction={sortBy === 'business_unit' ? sortDirection : 'asc'}
                      onClick={() => handleSort('business_unit')}
                    >
                      Business Unit
                    </TableSortLabel></TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}><TableSortLabel
                      active={sortBy === 'job_title'}
                      direction={sortBy === 'job_title' ? sortDirection : 'asc'}
                      onClick={() => handleSort('job_title')}
                    >
                      Job Title
                    </TableSortLabel></TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}><TableSortLabel
                      active={sortBy === 'department'}
                      direction={sortBy === 'department' ? sortDirection : 'asc'}
                      onClick={() => handleSort('department')}
                    >
                      Department
                    </TableSortLabel></TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}><TableSortLabel
                      active={sortBy === 'office_location'}
                      direction={sortBy === 'office_location' ? sortDirection : 'asc'}
                      onClick={() => handleSort('office_location')}
                    >
                      Location
                    </TableSortLabel></TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}><TableSortLabel
                      active={sortBy === 'is_active'}
                      direction={sortBy === 'is_active' ? sortDirection : 'asc'}
                      onClick={() => handleSort('is_active')}
                    >
                      Status
                    </TableSortLabel></TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', textAlign:'center' }}><TableSortLabel
                      active={sortBy === 'is_admin'}
                      direction={sortBy === 'is_admin' ? sortDirection : 'asc'}
                      onClick={() => handleSort('is_admin')}
                    >
                      Is Admin
                    </TableSortLabel></TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem', textAlign:'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedUsers.map((user) => {
                  
                  const isItemSelected = selected.includes(user)
                  const labelId = `enhanced-table-checkbox-${user.id}`
                  return (
                  <TableRow 
                  key={user.id}
                  role="checkbox"
                  tabIndex={-1}
                  aria-checked={isItemSelected}
                  selected={isItemSelected}
                  hover
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        onClick={(event) => handleClick(event, user)}
                        slotProps={{
                          input: {
                          'aria-labelledby': labelId,
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>{user.name || 'Unknown'}</TableCell>
                    <TableCell>{user.email || 'No email'}</TableCell>
                    <TableCell>{user.role || 'user'}</TableCell>
                    <TableCell>{user.business_unit || 'Unassigned'}</TableCell>
                    <TableCell>{user.job_title || 'N/A'}</TableCell>
                    <TableCell>{user.department || 'N/A'}</TableCell>
                    <TableCell>{user.office_location || 'N/A'}</TableCell>
                    <TableCell>{user.is_active !== false ? 'Active' : 'Suspended'}</TableCell>
                    <TableCell sx={{textAlign: 'center'}}>{user.is_admin ? (
                      <CheckBox sx={{ color: 'green'}} />
                      ) : (<></>)}</TableCell>
                    <TableCell sx={{textAlign: 'center'}}>
                      <Tooltip title="View User">
                          <span>
                            <IconButton onClick={() => onAction(user, false)}>
                              <Visibility />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit User">
                          <span>
                            <IconButton onClick={() => onAction(user, true)}>
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete User">
                          <span>
                            <IconButton onClick={() => handleSingleDeleteClick(user)} sx={{ ml: 1 }}>
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                    </TableCell>
                  </TableRow>
                )})}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={processedUsers.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setRowsPerPage(parseInt(event.target.value, 10))}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10,15,20]}
              showLastButton={true}
              showFirstButton={true}
            />
          </TableContainer>
        </Box>
      )}

      <DialogWrapper open={bulkDeleteDialogOpen} onClose={handleBulkDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Bulk Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selected.length} selected users?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDeleteCancel} disabled={bulkDeleteSubmitting}>Cancel</Button>
          <Button onClick={handleBulkDeleteConfirm} color="error" variant="contained" disabled={bulkDeleteSubmitting}>
            Delete
          </Button>
        </DialogActions>
      </DialogWrapper>

      <DialogWrapper open={singleDeleteDialogOpen} onClose={handleSingleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {deletingUser?.name || deletingUser?.email || 'this user'}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSingleDeleteCancel} disabled={singleDeleteSubmitting}>Cancel</Button>
          <Button onClick={handleSingleDeleteConfirm} color="error" variant="contained" disabled={singleDeleteSubmitting}>
            Delete
          </Button>
        </DialogActions>
      </DialogWrapper>
    </>
  );
};

export default UsersTab;
